
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TableCell, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { MoreHorizontal, Edit, Trash2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Harvest } from '@/types/database.types';
import { format } from 'date-fns';

interface HarvestListItemProps {
  harvest: Harvest;
  onDeleteClick: (id: string) => void;
  showFarmInfo?: boolean;
}

const HarvestListItem: React.FC<HarvestListItemProps> = ({ 
  harvest, 
  onDeleteClick,
  showFarmInfo = false
}) => {
  const navigate = useNavigate();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'planned':
        return 'bg-blue-100 text-blue-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '–';
    return format(new Date(dateString), 'MMM dd, yyyy');
  };

  return (
    <TableRow>
      <TableCell>
        <Badge className={getStatusColor(harvest.status)}>
          {harvest.status.replace('_', ' ')}
        </Badge>
      </TableCell>
      <TableCell>
        {harvest.product_name || harvest.farm_product_name || '–'}
      </TableCell>
      {showFarmInfo && (
        <TableCell>
          {harvest.farm_farmer_name || '–'}
        </TableCell>
      )}
      <TableCell>
        {formatDate(harvest.expected_date)}
      </TableCell>
      <TableCell>
        {harvest.expected_quantity ? `${harvest.expected_quantity} kg` : '–'}
      </TableCell>
      <TableCell className="hidden md:table-cell">
        {harvest.quality_grade || '–'}
      </TableCell>
      <TableCell className="text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => navigate(`/harvests/${harvest.id}/edit`)}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => onDeleteClick(harvest.id)}
              className="text-red-600"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
};

export default HarvestListItem;
